/**
 * Lista intermediária do Resumo Diário — derivada de `IndividualReportList`.
 * Cada card representa um par `(data × momento da rotina)` já iniciado; botão
 * primário permite criar um novo resumo.
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { withAlert } from "react-alert";
import { withTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  ChildCare as ChildCareIcon,
  FiberManualRecord as LiveDotIcon,
  Send as SendIcon
} from "@material-ui/icons";

import BackButton from "../../components/BackButton";
import DailySummaryService from "../../services/DailySummaryService";
import {
  DS_STATUS,
  buildSavePayloadFromDraft,
  enrichSummaryFromDraft,
  saveDraft,
  statusLabel
} from "./wbOpsDailySummaryDraft";
import { Can } from "../../acl/can";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
`;

const SectionTitle = styled(Typography)`
  && {
    font-weight: 600;
    color: #00303b;
  }
`;

const GroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0 10px;
  &:first-of-type {
    margin-top: 4px;
  }
  .label {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #00303b;
  }
  .rule {
    flex: 1 1 auto;
    height: 1px;
    background: rgba(0, 48, 59, 0.12);
  }
  .count {
    font-size: 12px;
    color: #607681;
  }
  &[data-emphasis="today"] .label {
    color: ${({ theme }) => theme.color.primary.main};
  }
  &[data-emphasis="today"] .rule {
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.color.primary.main},
      ${({ theme }) => theme.color.primary.light}
    );
  }
`;

const livePulse = keyframes`
  0%   { box-shadow: 0 0 0 1px rgba(211, 47, 47, 0.55),
                     0 0 0 0 rgba(211, 47, 47, 0.55),
                     0 2px 6px rgba(211, 47, 47, 0.15); }
  50%  { box-shadow: 0 0 0 1px rgba(211, 47, 47, 0.35),
                     0 0 0 10px rgba(211, 47, 47, 0),
                     0 4px 14px rgba(211, 47, 47, 0.22); }
  100% { box-shadow: 0 0 0 1px rgba(211, 47, 47, 0.55),
                     0 0 0 0 rgba(211, 47, 47, 0),
                     0 2px 6px rgba(211, 47, 47, 0.15); }
`;

const livePulseStyles = css`
  animation: ${livePulse} 1.8s ease-in-out infinite;
`;

const Card = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 12px 14px 14px;
  margin-bottom: 12px;
  background: #ffffff;
  border: 1px solid rgba(0, 48, 59, 0.12);
  border-left: 4px solid rgba(0, 48, 59, 0.12);
  border-radius: 10px;
  cursor: pointer;
  outline: none;
  font-family: inherit;
  box-shadow: 0 1px 2px rgba(0, 48, 59, 0.06), 0 1px 3px rgba(0, 48, 59, 0.04);
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    background 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: rgba(0, 48, 59, 0.22);
    box-shadow: 0 2px 4px rgba(0, 48, 59, 0.08), 0 3px 8px rgba(0, 48, 59, 0.06);
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.color.primary.main};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.primary.light};
  }

  &[data-status="done"] {
    border-left-color: #2e7d32;
    background: rgba(46, 125, 50, 0.04);
  }

  &[data-status="ready"] {
    border-left-color: #1565c0;
    background: rgba(21, 101, 192, 0.06);
  }

  &[data-status="current"] {
    border-color: rgba(211, 47, 47, 0.55);
    border-left-color: #d32f2f;
    background: rgba(211, 47, 47, 0.06);
    ${livePulseStyles}
  }

  &[data-status="current"]:hover {
    border-color: rgba(211, 47, 47, 0.75);
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-status="current"] {
      animation: none;
      box-shadow: 0 0 0 1px rgba(211, 47, 47, 0.45),
        0 2px 6px rgba(211, 47, 47, 0.15);
    }
  }
`;

const MetaStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  width: 100%;
`;

const MetaInline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 14px;
  flex: 1 1 auto;
  min-width: 0;
`;

const IconSquare = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.primary.light};
  color: ${({ theme }) => theme.color.primary.main};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  [data-status="done"] & {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
  }

  [data-status="current"] & {
    background: rgba(211, 47, 47, 0.12);
    color: #b71c1c;
  }
`;

const CardTitle = styled(Typography).attrs({
  variant: "subtitle1",
  component: "div"
})`
  && {
    font-weight: 600;
    color: #00303b;
    line-height: 1.25;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 4px;
  }
`;

const FooterStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding-top: 2px;
`;

const EmptyState = styled.div`
  background: #ffffff;
  border: 1px dashed rgba(0, 48, 59, 0.2);
  border-radius: 10px;
  padding: 40px 24px;
  text-align: center;
  color: #607681;
`;

function metaInline(label, value) {
  return (
    <Box
      component="span"
      display="inline-flex"
      alignItems="baseline"
      style={{ gap: 4, minWidth: 0 }}
      title={value != null ? String(value) : undefined}
    >
      <Typography
        variant="caption"
        color="textSecondary"
        component="span"
        style={{ lineHeight: 1.2 }}
      >
        {`${label}:`}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        style={{
          fontWeight: 500,
          color: "#00303b",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 220
        }}
      >
        {value != null && value !== "" ? value : "—"}
      </Typography>
    </Box>
  );
}

function stateOf(summary) {
  if (summary.status === DS_STATUS.SENT) return "done";
  if (summary.moment && summary.moment.current) return "current";
  if (summary.status === DS_STATUS.READY || (summary.filledPercent || 0) >= 1) {
    return "ready";
  }
  if ((summary.filledPercent || 0) >= 1) return "done";
  return "default";
}

function formatMomentWindow(momentDef) {
  if (!momentDef) return null;
  const start = momentDef.startTime || momentDef.horaInicio;
  const end = momentDef.endTime || momentDef.horaFim;
  if (!start || !end) return null;
  return `${String(start).slice(0, 5)} – ${String(end).slice(0, 5)}`;
}

class DailySummaryList extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, summaries: [] };
  }

  componentDidMount() {
    this.loadSummaries();
  }

  loadSummaries = async () => {
    const { match, alert, t } = this.props;
    const classId = match.params.id;
    this.setState({ loading: true });
    try {
      const summaries = await DailySummaryService.listSummaries(classId);
      const list = Array.isArray(summaries) ? summaries : [];
      const enriched = list.map(s => enrichSummaryFromDraft(s, classId));
      this.setState({ summaries: enriched });
    } catch (e) {
      console.error(e);
      alert.show(t("dailySummary.error.list"), {
        title: t("common.actions.error")
      });
      this.setState({ summaries: [] });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSend = async (e, summary) => {
    e.stopPropagation();
    const { match, alert, t } = this.props;
    const classId = match.params.id;
    if (summary.status !== DS_STATUS.READY) return;

    const payload = buildSavePayloadFromDraft(summary.draft, classId);
    if (!payload) {
      alert.show(t("dailySummary.noLocalDraftOpen"), {
        title: t("common.actions.attention")
      });
      return;
    }

    this.setState({ loading: true });
    try {
      await DailySummaryService.save(payload);
      saveDraft({
        ...summary.draft,
        classId,
        status: DS_STATUS.SENT
      });
      alert.show(t("dailySummary.sent"), {
        title: t("common.actions.success")
      });
      await this.loadSummaries();
    } catch (err) {
      console.error(err);
      alert.show(t("dailySummary.error.send"), {
        title: t("common.actions.error")
      });
      this.setState({ loading: false });
    }
  };

  buildEditUrl = summary => {
    const { match } = this.props;
    const classId = match.params.id;
    const date = encodeURIComponent(summary.date);
    const momentId = encodeURIComponent(summary.moment.id);
    return `/classes/${encodeURIComponent(
      classId
    )}/dailySummary/edit?date=${date}&moment=${momentId}`;
  };

  handleNew = () => {
    const { history, match } = this.props;
    history.push(
      `/classes/${encodeURIComponent(match.params.id)}/dailySummary/new`
    );
  };

  handleOpen = summary => {
    const { history } = this.props;
    history.push(this.buildEditUrl(summary));
  };

  renderCard = summary => {
    const { t } = this.props;
    const filled = Math.round((summary.filledPercent || 0) * 100);
    const cardState = stateOf(summary);
    const updatedLabel = summary.updatedAt
      ? moment(summary.updatedAt).fromNow()
      : "—";
    const canSend = summary.status === DS_STATUS.READY;

    let statusChip = null;
    if (summary.status === DS_STATUS.SENT || cardState === "done") {
      statusChip = (
        <Chip
          size="small"
          icon={<CheckCircleIcon style={{ color: "#2e7d32" }} />}
          label={t("dailySummary.status.sent")}
          style={{
            background: "rgba(46, 125, 50, 0.10)",
            color: "#1b5e20",
            fontWeight: 600,
            height: 22
          }}
        />
      );
    } else if (canSend) {
      statusChip = (
        <Chip
          size="small"
          icon={<SendIcon style={{ color: "#1565c0" }} />}
          label={statusLabel(DS_STATUS.READY)}
          style={{
            background: "rgba(21, 101, 192, 0.12)",
            color: "#0d47a1",
            fontWeight: 600,
            height: 22
          }}
        />
      );
    } else if (cardState === "current") {
      statusChip = (
        <Chip
          size="small"
          icon={<LiveDotIcon style={{ color: "#d32f2f" }} />}
          label={t("dailySummary.status.current")}
          style={{
            background: "rgba(211, 47, 47, 0.12)",
            color: "#b71c1c",
            fontWeight: 600,
            height: 22
          }}
        />
      );
    } else if (summary.status === DS_STATUS.DRAFT && filled > 0) {
      statusChip = (
        <Chip
          size="small"
          label={t("dailySummary.status.draft")}
          style={{
            background: "rgba(0, 48, 59, 0.08)",
            color: "#00303b",
            fontWeight: 600,
            height: 22
          }}
        />
      );
    }

    return (
      <Card
        type="button"
        key={summary.summaryKey}
        data-status={cardState}
        onClick={() => this.handleOpen(summary)}
        aria-label={t("dailySummary.openSummary", {
          date: summary.dateLabel,
          moment: summary.moment.name
        })}
      >
        <MetaStrip>
          <IconSquare>
            <ChildCareIcon />
          </IconSquare>
          <CardTitle>{summary.moment.name}</CardTitle>
          <MetaInline>
            {statusChip}
            {metaInline(t("common.labels.date"), summary.dateLabel)}
            {formatMomentWindow(summary.moment)
              ? metaInline(
                  t("common.labels.time"),
                  formatMomentWindow(summary.moment)
                )
              : null}
            {typeof summary.studentsCount === "number"
              ? metaInline(
                  t("dailySummary.labels.students"),
                  summary.studentsCount
                )
              : null}
            {typeof summary.indexesCount === "number"
              ? metaInline(t("dailySummary.labels.axes"), summary.indexesCount)
              : null}
            {metaInline(t("dailySummary.updated"), updatedLabel)}
          </MetaInline>
          {canSend ? (
            <Can I="send" a="dailySummary">
              <Tooltip title={t("dailySummary.send")}>
                <IconButton
                  color="primary"
                  size="small"
                  aria-label={t("dailySummary.send")}
                  onClick={e => this.handleSend(e, summary)}
                  style={{ flexShrink: 0 }}
                >
                  <SendIcon />
                </IconButton>
              </Tooltip>
            </Can>
          ) : null}
          <Button
            color="primary"
            variant="outlined"
            size="small"
            onClick={e => {
              e.stopPropagation();
              this.handleOpen(summary);
            }}
            style={{ flexShrink: 0, textTransform: "none" }}
          >
            {t("dailySummary.viewEdit")}
          </Button>
        </MetaStrip>

        {cardState !== "done" ? (
          <FooterStrip>
            <Tooltip title={t("dailySummary.filledPercent", { pct: filled })}>
              <LinearProgress
                variant="determinate"
                value={filled}
                style={{
                  flex: "1 1 auto",
                  height: 6,
                  borderRadius: 6,
                  background: "rgba(0, 48, 59, 0.08)"
                }}
              />
            </Tooltip>
            <Typography
              variant="caption"
              style={{ color: "#607681", minWidth: 96, textAlign: "right" }}
            >
              {t("dailySummary.filledPercent", { pct: filled })}
            </Typography>
          </FooterStrip>
        ) : null}
      </Card>
    );
  };

  renderGroups = summaries => {
    const { t } = this.props;
    const inProgress = summaries.filter(s => stateOf(s) === "current");
    const rest = summaries.filter(s => stateOf(s) !== "current");

    const groups = [];
    const groupIndex = new Map();
    rest.forEach(summary => {
      const key = summary.dateLabel;
      if (!groupIndex.has(key)) {
        groupIndex.set(key, groups.length);
        groups.push({ label: key, items: [] });
      }
      groups[groupIndex.get(key)].items.push(summary);
    });

    const countLabel = count =>
      `${count} ${
        count === 1
          ? t("dailySummary.count.one")
          : t("dailySummary.count.other")
      }`;

    return (
      <>
        {inProgress.length > 0 ? (
          <Box mb={2}>
            <GroupTitle data-emphasis="today">
              <span className="label">
                {t("dailySummary.group.inProgress")}
              </span>
              <span className="rule" />
              <span className="count">{countLabel(inProgress.length)}</span>
            </GroupTitle>
            {inProgress.map(this.renderCard)}
          </Box>
        ) : null}
        {groups.map(group => (
          <Box key={group.label}>
            <GroupTitle
              data-emphasis={
                group.label === t("dailySummary.today") ? "today" : "default"
              }
            >
              <span className="label">{group.label}</span>
              <span className="rule" />
              <span className="count">{countLabel(group.items.length)}</span>
            </GroupTitle>
            {group.items.map(this.renderCard)}
          </Box>
        ))}
      </>
    );
  };

  render() {
    const { match, t } = this.props;
    const { loading, summaries } = this.state;
    const classId = match && match.params ? match.params.id : null;
    const backTo = classId
      ? `/classes/${encodeURIComponent(classId)}`
      : "/classes";

    return (
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BackButton to={backTo} />
          </Grid>
          <Grid item xs={12}>
            <Header>
              <Box>
                <SectionTitle variant="h6">
                  {t("dailySummary.title")}
                </SectionTitle>
                <Typography variant="body2" style={{ color: "#607681" }}>
                  {t("dailySummary.subtitle")}
                </Typography>
              </Box>
              <Can I="create" a="dailySummary">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.handleNew}
                  style={{ textTransform: "none" }}
                >
                  {t("dailySummary.new")}
                </Button>
              </Can>
            </Header>
          </Grid>
          <Grid item xs={12}>
            {loading ? (
              <Box textAlign="center" py={6}>
                <CircularProgress />
              </Box>
            ) : null}
            {!loading && summaries.length === 0 ? (
              <EmptyState>
                <Typography variant="body1" style={{ marginBottom: 8 }}>
                  {t("dailySummary.empty")}
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 16 }}>
                  {t("dailySummary.emptyHint")}
                </Typography>
                <Can I="create" a="dailySummary">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={this.handleNew}
                    style={{ textTransform: "none" }}
                  >
                    {t("dailySummary.new")}
                  </Button>
                </Can>
              </EmptyState>
            ) : null}
            {!loading && summaries.length > 0 ? (
              <Box>{this.renderGroups(summaries)}</Box>
            ) : null}
          </Grid>
        </Grid>
      </Box>
    );
  }
}

DailySummaryList.propTypes = {
  alert: PropTypes.shape({ show: PropTypes.func }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    goBack: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string })
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(DailySummaryList)));
